import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { Shield, AlertTriangle } from 'lucide-react';
import { EmptyState } from '@/components/system/EmptyState';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const fmtDate = (d: string) => {
  try { return format(new Date(d), 'dd/MM/yy HH:mm', { locale: ptBR }); } catch { return d; }
};

const ACAO_COLORS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  criar: 'default',
  atualizar: 'secondary',
  excluir: 'destructive',
  aprovar: 'default',
  rejeitar: 'destructive',
  enviar: 'outline',
  SEGREGACAO_FUNCAO_EXCECAO: 'destructive',
};

export const EmpAuditoriaView: React.FC = () => {
  const [entidadeFilter, setEntidadeFilter] = useState('all');
  const [onlySod, setOnlySod] = useState(false);

  const { data: eventos = [], isLoading } = useQuery({
    queryKey: ['emp-auditoria-all', entidadeFilter, onlySod],
    queryFn: async () => {
      let query = (supabase as any)
        .from('emp_auditoria')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(200);

      if (entidadeFilter !== 'all') {
        query = query.eq('entidade', entidadeFilter);
      }

      if (onlySod) {
        query = query.eq('acao', 'SEGREGACAO_FUNCAO_EXCECAO');
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
  });

  const isSodEvent = (ev: any) => ev.acao === 'SEGREGACAO_FUNCAO_EXCECAO';

  // Distinct entities for filter
  const entidades = [...new Set(eventos.map((e: any) => e.entidade as string))].sort();
  const sodCount = eventos.filter(isSodEvent).length;

  if (isLoading) {
    return <div className="space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Total Eventos</p><p className="text-2xl font-bold">{eventos.length}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Entidades Distintas</p><p className="text-2xl font-bold">{entidades.length}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Últimas 24h</p><p className="text-2xl font-bold">{eventos.filter((e: any) => new Date(e.created_at) > new Date(Date.now() - 86400000)).length}</p></CardContent></Card>
        <Card className="border-destructive/30">
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 text-destructive" /> Exceções SoD
            </p>
            <p className="text-2xl font-bold text-destructive">{sodCount}</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <Select value={entidadeFilter} onValueChange={setEntidadeFilter}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Filtrar entidade" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
        {entidades.map((e: string) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <Switch
            id="sod-filter"
            checked={onlySod}
            onCheckedChange={setOnlySod}
          />
          <Label htmlFor="sod-filter" className="text-sm cursor-pointer flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
            Exibir somente eventos com exceção de SoD
          </Label>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Shield className="h-4 w-4" /> Trilha de Auditoria</CardTitle></CardHeader>
        <CardContent className="p-0">
          {eventos.length === 0 ? (
            <EmptyState
              text={onlySod ? 'Nenhum evento de exceção de SoD registrado.' : 'Nenhum evento de auditoria registrado.'}
              icon={Shield}
            />
          ) : (
            <Table className="table-zebra">
              <TableHeader>
                <TableRow>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>Entidade</TableHead>
                  <TableHead>Ação</TableHead>
                  <TableHead>Resumo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {eventos.map((ev: any) => (
                  <TableRow
                    key={ev.id}
                    className={isSodEvent(ev) ? 'bg-destructive/5 dark:bg-destructive/10' : ''}
                  >
                    <TableCell className="text-xs whitespace-nowrap">{fmtDate(ev.created_at)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{ev.entidade}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {isSodEvent(ev) && <AlertTriangle className="h-3 w-3 text-destructive" />}
                        <Badge variant={ACAO_COLORS[ev.acao] || 'outline'} className="text-xs">{ev.acao}</Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm max-w-[300px]">
                      {isSodEvent(ev) && ev.dados_resumo ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-xs italic cursor-help text-destructive font-medium truncate block max-w-[280px]">
                              Exceção SoD — Perfis: {
                                Array.isArray(ev.dados_resumo?.perfil_acumulado)
                                  ? ev.dados_resumo.perfil_acumulado.join(' + ')
                                  : '—'
                              }
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-xs">
                            <pre className="whitespace-pre-wrap">{JSON.stringify(ev.dados_resumo, null, 2)}</pre>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <span className="truncate block">
                          {ev.dados_resumo ? JSON.stringify(ev.dados_resumo).slice(0, 80) : '—'}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
